import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { 
  Flame, 
  Shield, 
  Lock, 
  Eye, 
  Heart, 
  Users, 
  Sparkles,
  ArrowRight,
  Quote,
  History,
  Home,
  Building2
} from 'lucide-react'

const coreValues = [
  {
    icon: Users,
    title: 'Universal Accessibility',
    description: 'Powerful AI features available to people of ANY socioeconomic class. Technology should empower everyone, not just those with resources.',
  },
  {
    icon: Lock,
    title: 'Privacy by Design',
    description: 'No export or transfer of personal, health, or financial data to ANY third party. Your data stays yours—inside your home or organization.',
  },
  {
    icon: Shield,
    title: 'Transparency & Compliance',
    description: 'Ethically devised solutions with clear assurances of how AI operates. We believe trust is built through openness.',
  },
  {
    icon: Eye,
    title: 'Responsible Profitability',
    description: 'Open-source foundations with sustainable business practices. Success measured not just in profit, but in positive impact.',
  },
]

const useCases = [
  {
    title: 'Craft Ideas',
    description: 'Transform thoughts into tangible concepts with AI-assisted creativity.',
  },
  {
    title: 'Build Visions',
    description: 'Collaborate with intelligent systems to architect your future.',
  },
  {
    title: 'Solve Problems',
    description: 'Tackle day-to-day challenges with enhanced decision-making.',
  },
  {
    title: 'Tell Stories',
    description: 'Preserve and share family narratives across generations.',
  },
  {
    title: 'Find Perspective',
    description: 'Add dimensions of understanding to mental health and wellbeing.',
  },
  {
    title: 'Make Decisions',
    description: 'Navigate complexity with informed, AI-augmented judgment.',
  },
]

export default function PhilosophyPage() {
  return (
    <div className="pt-24 pb-16">
      {/* Hero Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-synth-950/50 via-void-950 to-ember-950/20" />
        <div className="absolute inset-0 grid-overlay opacity-30" />
        
        {/* Cave art inspired decorative element */}
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-5">
          <svg viewBox="0 0 400 600" className="w-full h-full">
            <path 
              d="M50 100 Q100 50 150 100 T250 100 T350 100" 
              stroke="currentColor" 
              strokeWidth="3" 
              fill="none"
              className="text-synth-400"
            />
            <circle cx="100" cy="200" r="30" fill="currentColor" className="text-ember-500" />
            <circle cx="200" cy="180" r="25" fill="currentColor" className="text-synth-500" />
            <circle cx="300" cy="220" r="35" fill="currentColor" className="text-signal-500" />
            <path 
              d="M80 350 L120 300 L160 350 L200 280 L240 350 L280 310 L320 350" 
              stroke="currentColor" 
              strokeWidth="2" 
              fill="none"
              className="text-void-400"
            />
          </svg>
        </div>
        
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-ember-900/30 border border-ember-700/30 text-ember-400 text-sm font-medium mb-6">
              <Flame className="w-4 h-4" />
              Governing Philosophy
            </span>
            
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-display font-bold text-void-50 mb-8 leading-tight">
              The World's Oldest<br />
              <span className="text-gradient">Enduring Communication</span>
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Origin Story */}
      <section className="py-20 relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-synth-500 via-ember-500 to-signal-500 rounded-full" />
            
            <div className="pl-8 space-y-8">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-synth-900/50 border border-synth-700/30 shrink-0">
                  <History className="w-6 h-6 text-synth-400" />
                </div>
                <div>
                  <h2 className="text-2xl font-display font-bold text-void-100 mb-4">
                    Before Sumer, Before Persia
                  </h2>
                  <p className="text-void-300 leading-relaxed text-lg">
                    Long before the great civilizations—before Sumer or Persia, the Toltecs or the 
                    'New World'—humans have left messages that would exist beyond their speaking, 
                    to the generations.
                  </p>
                </div>
              </div>
              
              <div className="p-8 rounded-2xl bg-void-900/50 border border-void-800/50">
                <Quote className="w-10 h-10 text-synth-700 mb-4" />
                <p className="text-xl md:text-2xl text-void-200 leading-relaxed italic">
                  "Isolated, possibly by weather or natural event, possibly the need to survive 
                  predatory animals, some being picked up a rock or burnt wood and started 
                  depicting a message by projecting their ideas onto a cave or stone wall."
                </p>
              </div>
              
              <p className="text-void-300 leading-relaxed text-lg">
                These paleolithic panels and storyboards remain, and inspire, the need to return 
                to our ability to <span className="text-synth-400 font-medium">create</span> and{' '}
                <span className="text-synth-400 font-medium">endure</span>, and{' '}
                <span className="text-synth-400 font-medium">evolve</span>—with the changing 
                landscape of our digital medium and the dawn of general Artificial Intelligence.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Graffiti™ */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-void-950 via-synth-950/10 to-void-950" />
        
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-void-50 mb-6">
              Why We Chose <span className="text-gradient">Graffiti™</span>
            </h2>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="p-8 md:p-12 rounded-3xl bg-gradient-to-br from-synth-900/30 to-void-900 border border-synth-800/30"
          >
            <p className="text-xl md:text-2xl text-void-200 leading-relaxed mb-8">
              We want to empower our generation to grow a society and a civilization which is 
              comfortable with—and gaining efficiency by—the use of our models to help:
            </p>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {useCases.map((useCase, index) => (
                <motion.div
                  key={useCase.title}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="p-4 rounded-xl bg-void-950/50 border border-void-800/30"
                >
                  <h4 className="font-display font-semibold text-synth-400 mb-1">
                    {useCase.title}
                  </h4>
                  <p className="text-sm text-void-400">{useCase.description}</p>
                </motion.div>
              ))}
            </div>
            
            <div className="p-6 rounded-xl bg-ember-900/20 border border-ember-800/30">
              <p className="text-void-200 text-lg leading-relaxed">
                They should do this <span className="text-ember-400 font-semibold">inexpensively</span>,{' '}
                <span className="text-ember-400 font-semibold">safely</span>,{' '}
                <span className="text-ember-400 font-semibold">privately</span>, and with assurances of{' '}
                <span className="text-ember-400 font-semibold">transparency</span> and{' '}
                <span className="text-ember-400 font-semibold">compliance</span>.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* The Promise */}
      <section className="py-20 relative">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-synth-900/50 border border-synth-700/50 text-synth-300 text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              Our Promise
            </span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-void-50 mb-6">
              A FIRST User Experience in AI Adoption
            </h2>
            <p className="text-xl text-void-400 max-w-3xl mx-auto">
              Mimeograph Holdings presents Graffiti™—for anyone, anywhere, any time; without 
              the complicated problems of 7 billion opinions or 40,000 years of semantic noise.
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {coreValues.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 rounded-2xl bg-void-900/30 border border-void-800/50"
              >
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-xl bg-synth-900/50 border border-synth-700/30">
                    <value.icon className="w-6 h-6 text-synth-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-display font-semibold text-void-100 mb-2">
                      {value.title}
                    </h3>
                    <p className="text-void-400 leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Foundation */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-void-950 via-signal-950/10 to-void-950" />
        
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="p-8 md:p-12 rounded-3xl bg-void-900/50 border border-void-800/50"
          >
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-2xl md:text-3xl font-display font-bold text-void-50 mb-6">
                  Graffiti™ (Mimeograffiti)
                </h2>
                <p className="text-void-300 leading-relaxed mb-6">
                  An <span className="text-signal-400 font-semibold">Open-Source</span>,{' '}
                  <span className="text-signal-400 font-semibold">responsibly profitable</span>, and{' '}
                  <span className="text-signal-400 font-semibold">ethically devised</span> PaaS 
                  offering which through a unique networking approach, can safely live inside 
                  your homes or inside your companies.
                </p>
                
                <div className="p-4 rounded-xl bg-void-950/80 border border-void-700/30">
                  <p className="text-void-200 font-medium">
                    Without the need for any export or transfer of personal, health, or 
                    financial data to ANY third party.
                  </p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 rounded-xl bg-void-800/30 border border-void-700/30">
                  <div className="p-2 rounded-lg bg-synth-500/20">
                    <Home className="w-5 h-5 text-synth-400" />
                  </div>
                  <div>
                    <p className="text-void-200 font-medium">Home Deployment</p>
                    <p className="text-sm text-void-500">AI that lives safely in your home</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-4 rounded-xl bg-void-800/30 border border-void-700/30">
                  <div className="p-2 rounded-lg bg-signal-500/20">
                    <Building2 className="w-5 h-5 text-signal-400" />
                  </div>
                  <div>
                    <p className="text-void-200 font-medium">Enterprise Deployment</p>
                    <p className="text-sm text-void-500">AI that lives safely in your company</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-4 rounded-xl bg-void-800/30 border border-void-700/30">
                  <div className="p-2 rounded-lg bg-ember-500/20">
                    <Lock className="w-5 h-5 text-ember-400" />
                  </div>
                  <div>
                    <p className="text-void-200 font-medium">Zero Data Export</p>
                    <p className="text-sm text-void-500">Your data never leaves your control</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Closing Statement */}
      <section className="py-20 relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <div className="p-8 md:p-12 rounded-3xl bg-gradient-to-br from-ember-900/30 via-synth-900/20 to-signal-900/30 border border-synth-800/30">
              <Quote className="w-12 h-12 text-synth-700 mx-auto mb-6" />
              <p className="text-xl md:text-2xl text-void-100 leading-relaxed italic mb-8">
                "The steps we take now to set the tone and the character of the 
                'Age of Intelligent Machines' will remain on the walls of the inversion 
                which we enlist to augment our human potential."
              </p>
              <div className="w-16 h-1 bg-gradient-to-r from-synth-500 to-signal-500 mx-auto mb-6 rounded-full" />
              <p className="text-void-400">
                — The Founding Principle of Mimeograph Holdings
              </p>
            </div>
            
            <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/products" className="btn-primary flex items-center gap-2">
                Explore Graffiti™ Products
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/corporate/company/about" className="btn-secondary">
                About Mimeograph
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}